import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IoMdArrowDropdown } from 'react-icons/io';

import {
  MultiSelectActions,
  MultiSelectButton,
  MultiSelectContainer,
  MultiSelectGroups,
  MultiSelectList,
  MultiSelectOption,
  MultiSelectPanel,
} from './FilterSelector.style';

export interface IMultiSelectOption {
  value: string;
  label: string;
}

// Shortcut that toggles a set of values at once; `title` is shown on hover
export interface IMultiSelectGroup {
  label: string;
  values: string[];
  title?: string;
}

interface IProps {
  options: IMultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  allLabel: string;
  disabled?: boolean;
  groups?: IMultiSelectGroup[];
}

const GroupCheckbox: FC<{ checked: boolean; indeterminate: boolean; onChange: () => void }> = ({
  checked,
  indeterminate,
  onChange,
}) => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return <input ref={ref} type="checkbox" checked={checked} onChange={onChange} />;
};

const MultiSelect: FC<IProps> = ({ options, selected, onChange, allLabel, disabled, groups }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const buttonLabel = useMemo(() => {
    if (selected.length === 0) return allLabel;
    if (selected.length === 1) {
      return options.find(option => option.value === selected[0])?.label ?? '1 selected';
    }
    return `${selected.length} selected`;
  }, [selected, options, allLabel]);

  const toggleOption = useCallback(
    (value: string) => {
      onChange(
        selectedSet.has(value) ? selected.filter(item => item !== value) : [...selected, value]
      );
    },
    [onChange, selected, selectedSet]
  );

  const toggleGroup = useCallback(
    (group: IMultiSelectGroup) => {
      const allSelected = group.values.every(value => selectedSet.has(value));
      if (allSelected) {
        const groupValues = new Set(group.values);
        onChange(selected.filter(item => !groupValues.has(item)));
      } else {
        onChange([...selected, ...group.values.filter(value => !selectedSet.has(value))]);
      }
    },
    [onChange, selected, selectedSet]
  );

  const handleSelectAll = useCallback(
    () => onChange(options.map(option => option.value)),
    [onChange, options]
  );
  const handleClear = useCallback(() => onChange([]), [onChange]);

  return (
    <MultiSelectContainer ref={containerRef}>
      <MultiSelectButton
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen(prev => !prev)}
        title={
          selected.length > 1
            ? selected
                .map(value => options.find(o => o.value === value)?.label)
                .filter(Boolean)
                .join('\n')
            : undefined
        }
      >
        <span>{buttonLabel}</span>
        <IoMdArrowDropdown />
      </MultiSelectButton>
      {isOpen && (
        <MultiSelectPanel>
          <MultiSelectActions>
            <button type="button" onClick={handleSelectAll}>
              Select all
            </button>
            <button type="button" onClick={handleClear}>
              Clear
            </button>
          </MultiSelectActions>
          <MultiSelectList>
            {groups && groups.length > 0 && (
              <MultiSelectGroups>
                {groups.map(group => {
                  const selectedCount = group.values.filter(value => selectedSet.has(value)).length;
                  const allSelected =
                    group.values.length > 0 && selectedCount === group.values.length;
                  return (
                    <MultiSelectOption key={group.label} title={group.title}>
                      <GroupCheckbox
                        checked={allSelected}
                        indeterminate={selectedCount > 0 && !allSelected}
                        onChange={() => toggleGroup(group)}
                      />
                      <span>{group.label}</span>
                    </MultiSelectOption>
                  );
                })}
              </MultiSelectGroups>
            )}
            {options.map(option => (
              <MultiSelectOption key={option.value}>
                <input
                  type="checkbox"
                  checked={selectedSet.has(option.value)}
                  onChange={() => toggleOption(option.value)}
                />
                <span>{option.label}</span>
              </MultiSelectOption>
            ))}
          </MultiSelectList>
        </MultiSelectPanel>
      )}
    </MultiSelectContainer>
  );
};

export default MultiSelect;
